import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];
  #observer;
  #frame;
  #progress = 0.35;
  #lastLayout = '';

  connectedCallback() {
    super.connectedCallback();
    const { content } = this.refs;
    if (!content.firstElementChild?.children.length) return;
    this.#refresh();
    window.addEventListener('resize', this.#handleResize);
    this.#observer = new ResizeObserver(this.#handleResize);
    this.#observer.observe(this);
    this.#observer.observe(content.firstElementChild);
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#refresh();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#handleResize.cancel();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #handleResize = debounce(() => this.#refresh(), 100);

  #refresh() {
    const { content, wrapper } = this.refs;
    const original = content.firstElementChild;
    if (!(original instanceof HTMLElement)) return;
    const width = this.getBoundingClientRect().width;
    const itemWidth = original.getBoundingClientRect().width;
    if (!width || !itemWidth) return;
    const gap = parseFloat(getComputedStyle(content).columnGap) || 0;
    const signature = `${width}:${itemWidth}:${gap}`;
    if (signature === this.#lastLayout) return;
    this.#lastLayout = signature;

    const animation = wrapper.getAnimations()[0];
    if (animation) {
      const timing = animation.effect?.getComputedTiming();
      const duration = Number(timing?.duration);
      const time = Number(animation.currentTime);
      if (duration > 0 && Number.isFinite(time)) {
        this.#progress = ((time % duration) + duration) % duration / duration;
      }
    }

    // Fill one complete track, with an extra repeat for coverage.
    const copies = Math.max(1, Math.ceil((width + gap) / (itemWidth + gap)) + 1);
    while (content.children.length > copies) content.lastElementChild.remove();
    while (content.children.length < copies) {
      content.appendChild(this.#clone(original));
    }

    // Keep exactly two equal tracks for the existing seamless CSS loop.
    for (const child of Array.from(wrapper.children)) {
      if (child !== content) child.remove();
    }
    wrapper.appendChild(this.#clone(content));

    // Keep travel speed consistent regardless of screen width.
    const factor = Number(this.getAttribute('data-speed-factor')) || 20;
    const distance = content.getBoundingClientRect().width + gap;
    const duration = Math.max(1, distance / (factor * 2));
    this.style.setProperty('--marquee-speed', `${duration}s`);
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      for (const runningAnimation of wrapper.getAnimations()) {
        runningAnimation.currentTime = duration * 1000 * this.#progress;
        runningAnimation.updatePlaybackRate(1);
      }
    });
  }

  #clone(element) {
    const clone = element.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    for (const node of [clone, ...clone.querySelectorAll('*')]) {
      node.removeAttribute('id');
      node.removeAttribute('ref');
    }
    for (const node of clone.querySelectorAll('a, button, input, select, textarea, [tabindex]')) {
      node.setAttribute('tabindex', '-1');
    }
    return clone;
  }
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}